import dayjs from "dayjs";
import moment from "moment";
// var timezone = require("dayjs/plugin/timezone");
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const convertDateToSODTimestamp = (date) => {
  let dateString = date;
  const result = dayjs(dateString).format(getDateFormat());
  const timestamp = dayjs(result).startOf('day').toDate().getTime();
  return timestamp;
};

export const convertDateToEODTimestamp = (date) => {
  let dateString = date;
  const result = dayjs(dateString).format(getDateFormat());
  const timestamp = dayjs(result).endOf('day').toDate().getTime();
  return timestamp;
};

export const convertTimeToTimestamp = (dateTime) => {
  let dateTimeString = dateTime;
  const timestamp = dayjs(dateTimeString).toDate().getTime();
  return timestamp;
};

export const getDateFormat = () => {
  return "MM/DD/YYYY";
}

export const mergeDateAndTime = (activityDate, activityTime) => {
  const splittedDateAndTime = activityTime?.split("T");
  return dayjs(activityDate).format("YYYY-MM-DD") + "T" + splittedDateAndTime[1]
}

const MILLISECONDS_IN_AN_HOUR = 60 * 60 * 1000;

// No maximum visit length existed before overnight visits were supported, when the
// service date alone bounded a visit. Rollover removes that bound, so this caps the
// accidental long visits it makes possible (for example 10:00 AM to 9:00 AM).
export const MAX_VISIT_DURATION_HOURS = 12;

const toTimestamp = (value) => {
  if (value === null || value === undefined || value === "") {
    return NaN;
  }
  const timestamp = Number.isFinite(value) ? value : dayjs(value).valueOf();
  return Number.isFinite(timestamp) ? timestamp : NaN;
};

/**
 * Anchors a time picked on the visit form to the service date, so start and end are
 * always full timestamps instead of time-of-day values.
 */
export const buildVisitTimestamp = (visitDate, time) => {
  const pickedTimestamp = toTimestamp(time);
  if (Number.isNaN(pickedTimestamp)) {
    return null;
  }
  if (!visitDate) {
    return pickedTimestamp;
  }

  const timestamp = toTimestamp(
    mergeDateAndTime(visitDate, dayjs(pickedTimestamp).format()),
  );
  return Number.isNaN(timestamp) ? null : timestamp;
};

/**
 * A visit may run past midnight, so an end that falls before its start belongs to the
 * next day. Equal timestamps are left untouched, they are rejected as invalid instead
 * of silently becoming a 24 hour visit.
 */
export const normalizeVisitEndTimestamp = (startTimestamp, endTimestamp) => {
  const start = toTimestamp(startTimestamp);
  const end = toTimestamp(endTimestamp);

  if (Number.isNaN(start) || Number.isNaN(end) || end >= start) {
    return endTimestamp ?? null;
  }

  return dayjs(end).add(1, "day").valueOf();
};

/**
 * Validates a start/end pair that has already been normalized. Returns null when the
 * pair is valid, or when either value is absent, since required fields are reported
 * separately.
 */
export const getVisitTimeValidationError = (startTimestamp, endTimestamp) => {
  if (!startTimestamp || !endTimestamp) {
    return null;
  }

  const start = toTimestamp(startTimestamp);
  const end = toTimestamp(endTimestamp);

  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return "Please select a valid start time and end time.";
  }

  if (start === end) {
    return "Start time and end time cannot be the same.";
  }

  if (end - start > MAX_VISIT_DURATION_HOURS * MILLISECONDS_IN_AN_HOUR) {
    return `A visit cannot be longer than ${MAX_VISIT_DURATION_HOURS} hours. Please check the start time and end time.`;
  }

  return null;
};

export const getCurrentTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const getOrganizationTimestamp = (
  userTimestamp,
  organizationTimeZone,
) => {
  const currentTimeZone = getCurrentTimeZone();

  const local = dayjs(userTimestamp).tz(currentTimeZone);

  return dayjs
    .tz(
      local.format("YYYY-MM-DD HH:mm:ss"),
      "YYYY-MM-DD HH:mm:ss",
      organizationTimeZone,
    )
    .valueOf();
};

export function getLocalTimeZoneDateFormat() {
  const currentTimeZone = getCurrentTimeZone()
  if (/Asia/.test(currentTimeZone)) {
    return 'DD/MM/YYYY'
  } else if (/America/.test(currentTimeZone)) {
    return 'MM/DD/YYYY'
  }
}

export const getDateString = (timestamp) => {
  const date = timestamp ? new Date(timestamp) : new Date();
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })
}

export const getTimeString = (timestamp) => {
  const date = new Date(timestamp);
  const options = { hour: "2-digit", minute: "2-digit", hour12: true };
  const time = date.toLocaleTimeString("en-US", options);

  return time;
};

export const dateAndTime = (timestamp) => {
  const date = new Date(timestamp);
  const formattedDate = date.toLocaleDateString();
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formattedDate} ${formattedTime}`;
};

export const formatTime = (timestamp, timeStr) => {
  if (timestamp) {
    return moment(timestamp).format("h:mm A");
  }
  if (timeStr) {
    // Handle "HH:mm" or "HH:mm:ss"
    return moment(timeStr, "HH:mm").format("h:mm A");
  }
  return null;
};

export const getTimeDifferenceString = (timestamp) => {
  if (!timestamp) return null;

  const start = new Date(timestamp);
  const now = new Date();

  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();

  // Adjust days if negative
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0); // last day of previous month
    days += prevMonth.getDate();
  }

  // Adjust months if negative
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  // Collect only non-zero parts
  const parts = [];
  if (years) parts.push(`${years} year${years !== 1 ? "s" : ""}`);
  if (months) parts.push(`${months} month${months !== 1 ? "s" : ""}`);
  if (days) parts.push(`${days} day${days !== 1 ? "s" : ""}`);

  return parts.join(", ") || "0 days"; // fallback if everything is zero
};

export const getTimeSpentFromTimestamps = (startTimestamp, endTimestamp) => {
  // Validate inputs
  if (!startTimestamp || !endTimestamp) {
    return null;
  }

  // End timestamps are normalized to the next day when a visit crosses midnight, so a
  // negative difference means the pair is invalid rather than overnight.
  const diffMs = endTimestamp - startTimestamp;
  if (diffMs < 0) {
    return null;
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}h ${minutes}m`;
}
